import { KeyValuePipe } from '@angular/common';
import { ChangeDetectionStrategy,Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOption } from '@angular/material/core';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';

import { AdminComponentBase, PageSimpleContentComponent, PageStatus } from 'app/layout/content';
import { AuthorizationContext, AuthorizationService } from 'app/shared/authentication';
import { AssetsEditorComponent, DeleteAssetEditorFile, UploadAssetEditor } from 'app/shared/components/assets-editor.component';
import { ErrorComponent } from 'app/shared/components/error.component';
import { SaveButtonComponent } from 'app/shared/components/save-button.component';
import { FileEditorInformation, GeneratedImageDefinition, GeneratedImageKind, GlobalPermission, SubscriptionPermission } from 'app/shared/graph';

import { GeneratedImageConfigEditorComponent } from './generated-image-config-editor.component';
import { GeneratedImageConfigUtil } from './generated-image-config-util.service';
import { GeneratedImagesService } from './generated-images.service';

enum GenerateImageViewMode {
    create,
    edit
}

enum Scope {
    global = 'global',
    subscription = 'subscription'
}

@Component({
    templateUrl: './generated-image-page.component.html',
    styleUrls: ['./generated-image-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, FormsModule, MatTabGroup, MatTab, MatFormField, MatLabel, MatInput, MatHint, MatError, MatSelect, MatOption, GeneratedImageConfigEditorComponent, ErrorComponent, SaveButtonComponent, MatCheckbox, KeyValuePipe, AssetsEditorComponent],
})
export class GeneratedImagePageComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly authorizationService = inject(AuthorizationService);
    public readonly generatedImagesService = inject(GeneratedImagesService);
    private readonly generatedImageConfigUtil = inject(GeneratedImageConfigUtil);

    private authorizationContext: AuthorizationContext;
    private generatedImageDefinitionId: any;
    private mode: GenerateImageViewMode = GenerateImageViewMode.create;

    @ViewChild('assetEditor', { static: false }) assetEditor: AssetsEditorComponent<FileEditorInformation>;

    subscriptionId?: string;
    scope: Scope;
    urlPattern = /^https:\/\/.+$/;
    invalidUrlMessage = 'This url is not valid.';
    generatedImageDefinition: GeneratedImageDefinition;
    generatedImageKinds: Record<GeneratedImageKind, string> = {
        [GeneratedImageKind.Generic]: 'Generic',
        [GeneratedImageKind.MapBox]: 'Mapbox',
        [GeneratedImageKind.QrCode]: 'QR Code',
    };
    mapBoxAccessKey?: string;

    ngOnInit(): void {
        this.authorizationService.authorizationContext().subscribe((authContext) => {
            this.authorizationContext = authContext;
        });

        this.registerSubscription(
            this.route.params.subscribe(params => {
                if (params.subscriptionId) {
                    this.scope = Scope.subscription;
                    this.subscriptionId = params.subscriptionId;
                } else {
                    this.scope = Scope.global;
                    this.subscriptionId = undefined;
                }

                if (params.generatedImageId) {
                    this.generatedImageDefinitionId = params.generatedImageId;
                    this.mode = GenerateImageViewMode.edit;
                    this.reload();
                } else {
                    this.mode = GenerateImageViewMode.create;
                    this.initNew();
                    this.setPageStatus(PageStatus.loaded);
                }
            }),
        );
    }

    ngOnDestroy() {
        this.unsubscribeSubscriptions();
    }

    canManage(): boolean {
        if (this.subscriptionId) {
            return this.authorizationContext.hasSubscriptionPermissions(this.subscriptionId, SubscriptionPermission.ManageGeneratedImages);
        }
        return this.authorizationContext.hasGlobalPermissions(GlobalPermission.ManageGeneratedImages);
    }

    createOrUpdate() {
        if (this.generatedImageDefinition.kind === GeneratedImageKind.MapBox) {
            this.generatedImageConfigUtil.updateMapBoxApiKey(this.generatedImageDefinition.configs, this.mapBoxAccessKey);
        }
        if (this.mode == GenerateImageViewMode.edit) {
            this.update();
        } else if (this.mode == GenerateImageViewMode.create) {
            this.create();
        }
    }

    update() {
        this.subscribeWithGenericSavingErrorHandling(
            this.generatedImagesService.updateGeneratedImageDefinition({
                id: this.generatedImageDefinition.id,
                kind: this.generatedImageDefinition.kind,
                displayName: this.generatedImageDefinition.displayName,
                description: this.generatedImageDefinition.description,
                defaultAlias: this.generatedImageDefinition.defaultAlias,
                endpoint: this.generatedImageDefinition.endpoint,
                preserveAspectRatio: this.generatedImageDefinition.preserveAspectRatio,
                configs: this.generatedImageDefinition.configs,
                warning: this.generatedImageDefinition.warning,
                maxHeight: this.generatedImageDefinition.maxHeight,
                maxWidth: this.generatedImageDefinition.maxWidth,
            }),
            () => {
                this.reload();
            },
        );
    }

    create() {
        this.subscribeWithGenericSavingErrorHandling(this.generatedImagesService.createGeneratedImageDefinition(
                {
                    kind: this.generatedImageDefinition.kind,
                    displayName: this.generatedImageDefinition.displayName,
                    description: this.generatedImageDefinition.description,
                    defaultAlias: this.generatedImageDefinition.defaultAlias,
                    endpoint: this.generatedImageDefinition.endpoint,
                    preserveAspectRatio: this.generatedImageDefinition.preserveAspectRatio,
                    configs: this.generatedImageDefinition.configs,
                    subscriptionId: this.subscriptionId,
                    warning: this.generatedImageDefinition.warning,
                    maxHeight: this.generatedImageDefinition.maxHeight,
                    maxWidth: this.generatedImageDefinition.maxWidth,
                },
            ),
            () => {
                this.router.navigate(['..'], { relativeTo: this.route });
            },
        );
    }

    reload() {
        this.subscribeWithGenericLoadingErrorHandling(this.generatedImagesService.loadDefinition(this.generatedImageDefinitionId), generatedImageDefinition => {
            this.generatedImageDefinition = generatedImageDefinition;
            this.mapBoxAccessKey = this.generatedImageConfigUtil.getMapBoxAccessToken(generatedImageDefinition.configs);
        });
    }

    initNew() {
        this.generatedImageDefinition = {
            kind: GeneratedImageKind.Generic,
            id: '',
            version: '',
            displayName: '',
            defaultAlias: '',
            endpoint: '',
            preserveAspectRatio: false,
            fileEditorInformation: [],
            subscriptionId: this.subscriptionId,
            configs: {
                headers: [],
                additionalProperties: [],
                editorParameters: [],
            },
        };
    }

    protected uploadAsset(asset: UploadAssetEditor) {
        const input = 'content' in asset
            ? {
                definitionId: this.generatedImageDefinitionId,
                filename: asset.filename,
                content: asset.content,
            }
            : {
                definitionId: this.generatedImageDefinitionId,
                filename: asset.filename,
                contentBase64: asset.contentBase64,
            };
        this.subscribeWithGenericSavingErrorHandling(this.generatedImagesService.uploadAssetEditorFile(input), (result) => {
            this.updateAssetAfterUpload({
                ...result,
                version: result.version,
            });
        });
    }

    protected uploadAssets(assets: UploadAssetEditor[]) {
        // FIXME: Instead of doing multiple calls, consider batching uploads to improve performance, in request with multiple mutations
        this.subscribeWithGenericSavingErrorHandling(forkJoin(
            assets.map(asset => this.generatedImagesService.uploadAssetEditorFile({
                filename: asset.filename,
                contentBase64: 'content' in asset
                    ? btoa(asset.content)
                    : asset.contentBase64,
                definitionId: this.generatedImageDefinitionId
            })),
        ), (results) => {
            for (let result of results) {
                this.updateAssetAfterUpload({
                    ...result,
                    version: '',
                });
            }
        });
    }

    private updateAssetAfterUpload(updatedAssetEditorInfo: FileEditorInformation) {
        let assetIndex = this.generatedImageDefinition.fileEditorInformation.findIndex(x => x.filename == updatedAssetEditorInfo.filename);
        if (assetIndex != -1) {
            this.generatedImageDefinition.fileEditorInformation[assetIndex] = updatedAssetEditorInfo;
        } else {
            this.generatedImageDefinition.fileEditorInformation.push(updatedAssetEditorInfo);
        }
        this.assetEditor.updateFile(updatedAssetEditorInfo);
    }

    protected deleteAsset(asset: DeleteAssetEditorFile) {
        this.subscribeWithGenericSavingErrorHandling(this.generatedImagesService.deleteAssetEditorFile({
            filename: asset.filename,
            version: asset.version,
            definitionId: this.generatedImageDefinitionId,
        }), (_) => {
            let assetIndex = this.generatedImageDefinition.fileEditorInformation.findIndex(x => x.filename == asset.filename);
            if (assetIndex != -1) {
                this.generatedImageDefinition.fileEditorInformation.splice(assetIndex, 1);
            }
        });
    }
}
